import React, { ErrorInfo } from "react";

import { logError } from "../../appV2/lib/analytics";

interface State {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  componentStack?: string;
}

const IS_DEV = process.env.NODE_ENV === "development";

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ErrorBoundary extends React.Component<any, State> {
  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'props' implicitly has an 'any' type.
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: undefined };
  }

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'error' implicitly has an 'any' type.
  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ componentStack: errorInfo.componentStack ?? undefined });
    /**
     * Taken from https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#react-error-boundaries-instrumentation
     */
    const renderingError = new Error(error.message);
    renderingError.name = `ReactRenderingError`;
    renderingError.stack = errorInfo.componentStack;
    (renderingError as Error & { cause: Error }).cause = error;
    logError("React Error Boundary", { error: renderingError });
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div
        style={{
          fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding:
            "max(40px, env(safe-area-inset-top)) 20px max(40px, env(safe-area-inset-bottom))",
          background: "linear-gradient(160deg, #fff8f2 0%, #ffeedd 60%, #fff3ea 100%)",
          boxSizing: "border-box",
        }}
      >
        {/* Google font */}
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap"
        />

        {/* Card */}
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255,180,120,0.3)",
            borderRadius: 24,
            padding: "36px 28px 32px",
            boxShadow: "0 8px 40px rgba(180,80,20,0.08), 0 1px 4px rgba(180,80,20,0.05)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 20,
            boxSizing: "border-box",
          }}
        >
          {/* Warning badge */}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "rgba(220,60,20,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width={28}
              height={28}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#c94f1a"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          {/* Heading */}
          <p
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: "#1a0f05",
              textAlign: "center",
              lineHeight: 1.3,
              letterSpacing: "-0.02em",
              margin: 0,
            }}
          >
            Something went wrong
          </p>

          {/* Body */}
          <p
            style={{
              fontSize: 14,
              color: "#6b5040",
              textAlign: "center",
              lineHeight: 1.65,
              margin: 0,
            }}
          >
            Please reload and try again. If the problem continues,{" "}
            <a
              href="https://support.clipboardhealth.com/hc/en-us/requests/new"
              target="_blank"
              style={{
                color: "#c94f1a",
                textDecoration: "underline",
                textUnderlineOffset: 2,
              }}
            >
              contact support
            </a>{" "}
            or call <strong style={{ color: "#1a0f05" }}>408-837-0116</strong>.
          </p>

          {/* Reload button */}
          <button
            onClick={() => {
              window.location.reload();
            }}
            style={{
              marginTop: 4,
              width: "100%",
              padding: "14px 20px",
              background: "#1a0f05",
              color: "#fff",
              fontFamily: "inherit",
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: "0.01em",
              border: "none",
              borderRadius: 14,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            Reload page
          </button>

          {/* Dev-only stack trace */}
          {IS_DEV && (
            <details
              open
              style={{
                width: "100%",
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid rgba(200,100,40,0.2)",
              }}
            >
              <summary
                style={{
                  padding: "10px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#c94f1a",
                  background: "rgba(255,190,140,0.15)",
                  cursor: "pointer",
                  userSelect: "none",
                  listStyle: "none",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 6,
                  lineHeight: 1.4,
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 1 }}>▶</span>
                <span style={{ wordBreak: "break-word" }}>{error.message ?? String(error)}</span>
              </summary>
              <pre
                style={{
                  margin: 0,
                  padding: "12px 14px",
                  fontFamily: "'SF Mono', 'Fira Code', monospace",
                  fontSize: 11,
                  lineHeight: 1.6,
                  color: "#3d2010",
                  background: "rgba(255,245,235,0.9)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  overflowX: "hidden",
                  overflowY: "auto",
                  maxHeight: "50vh",
                }}
              >
                {error.stack ?? ""}
                {componentStack ? `\n\nComponent stack:${componentStack}` : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
